import { db, type CompanyRow, type SiteRow, type ContactRow, type UnitRow } from '../db';
import type { WorkingSetCounts, WorkingSetStatus } from '../provider/types';
import { ReportsRepo } from '../repos/reportsRepo';
import { catalogRepo } from '../repos/catalogRepo';
import { WORKING_SET_EMPTY_COUNTS, WORKING_SET_META_KEYS } from './workingSetSpec';

function isWorkingSetCounts(value: unknown): value is WorkingSetCounts {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.companies === 'number' &&
    typeof record.sites === 'number' &&
    typeof record.units === 'number' &&
    typeof record.contacts === 'number' &&
    typeof record.reports === 'number' &&
    typeof record.templates === 'number' &&
    typeof record.catalogs === 'number'
  );
}

export async function getWorkingSetStatus(): Promise<WorkingSetStatus> {
  const [lastRefreshEntry, countsEntry, readyEntry] = await Promise.all([
    db.meta.get(WORKING_SET_META_KEYS.lastRefreshAt),
    db.meta.get(WORKING_SET_META_KEYS.counts),
    db.meta.get(WORKING_SET_META_KEYS.isReady),
  ]);

  const lastRefreshAt = typeof lastRefreshEntry?.value === 'string' ? lastRefreshEntry.value : null;
  const counts = isWorkingSetCounts(countsEntry?.value)
    ? countsEntry?.value
    : WORKING_SET_EMPTY_COUNTS;
  const isReady =
    typeof readyEntry?.value === 'boolean' ? readyEntry.value : Boolean(lastRefreshAt);

  return {
    lastRefreshAt,
    counts,
    isReady,
  } satisfies WorkingSetStatus;
}

function isActiveCompany(row: CompanyRow): boolean {
  return !row.syncDeletedAt;
}

function isActiveSite(row: SiteRow): boolean {
  return !row.syncDeletedAt;
}

function isActiveContact(row: ContactRow): boolean {
  // Use syncDeletedAt as the single source of truth
  return !row.syncDeletedAt;
}

function isActiveUnit(row: UnitRow): boolean {
  // Use syncDeletedAt as the single source of truth
  return !row.syncDeletedAt;
}

export async function refreshWorkingSet(): Promise<WorkingSetStatus> {
  // Refresh order: assignments -> master data -> last finalized reports -> catalogs/templates.
  const sites = (await db.sites.toArray()).filter(isActiveSite);
  const siteIds = new Set(sites.map((site) => site.id));
  const companyIds = new Set(sites.map((site) => site.companyId));

  const companies = (await db.companies.toArray())
    .filter(isActiveCompany)
    .filter((company) => companyIds.has(company.id));

  const units = (await db.units.toArray())
    .filter(isActiveUnit)
    .filter((unit) => siteIds.has(unit.siteId)).length;

  const contacts = (await db.contacts.toArray())
    .filter(isActiveContact)
    .filter((contact) => companyIds.has(contact.companyId))
    .filter((contact) => {
      if (!contact.siteId) return true;
      return siteIds.has(contact.siteId);
    }).length;

  const lastFinalizedBySite: Record<string, string> = {};
  for (const site of sites) {
    const report = await ReportsRepo.getLatestFinalizedBySite(site.id);
    if (report) {
      lastFinalizedBySite[site.id] = report.id;
    }
  }

  const templatesCount = await db.templates.count();
  const catalogs = await catalogRepo.getAllCatalogs();

  const counts: WorkingSetCounts = {
    companies: companies.length,
    sites: sites.length,
    units,
    contacts,
    reports: Object.keys(lastFinalizedBySite).length,
    templates: templatesCount,
    catalogs: Object.keys(catalogs).length,
  };

  const now = new Date().toISOString();

  await db.transaction('rw', db.meta, async () => {
    await db.meta.put({ key: WORKING_SET_META_KEYS.lastRefreshAt, value: now });
    await db.meta.put({ key: WORKING_SET_META_KEYS.counts, value: counts });
    await db.meta.put({ key: WORKING_SET_META_KEYS.isReady, value: true });
    await db.meta.put({
      key: WORKING_SET_META_KEYS.lastFinalizedBySite,
      value: lastFinalizedBySite,
    });
    await db.meta.put({ key: WORKING_SET_META_KEYS.watermark, value: now });
  });

  return {
    lastRefreshAt: now,
    counts,
    isReady: true,
  } satisfies WorkingSetStatus;
}
